import React, { useCallback, useState } from "react";
import { Alert, Button, Modal, Select, Space, Typography } from "antd";
import { useFortiusClientService } from "../../../services/fortius/FortiusClientServiceProvider";
import { useClientConfigService } from "../../../services/clients/ClientConfigServiceProvider";
import { ClientSummary } from "../../../model/fortius/ClientSummary";
import {
    ClientSettings,
    DEFAULT_CHECKIN_DUPLICATE_WINDOW_MINUTES,
} from "../../../model/client/ClientSettings";
import { ClientSettingUpdate } from "../../../model/client/UpdateClientSettingsRequest";

const { Text } = Typography;

export type CopyGroup = 'fees' | 'forms' | 'banners' | 'checkin';

// Maps each config group to the client_setting keys it copies, reading the values
// off a source tenant's settings. Defaults match the backend so an unset source key
// still copies a valid value rather than undefined. Most are opaque JSON blobs; the
// check-in group also carries a whole-number key the API validates on write.
const GROUP_ROWS: Record<CopyGroup, (s: ClientSettings) => ClientSettingUpdate[]> = {
    fees: (s) => [{ key: 'fees.joining', value: s.fees?.joiningJson ?? '{}', valueType: 'json' }],
    forms: (s) => [
        { key: 'forms.health', value: s.forms?.healthJson ?? '{}', valueType: 'json' },
        { key: 'forms.newMember', value: s.forms?.newMemberJson ?? '{}', valueType: 'json' },
        { key: 'forms.signup', value: s.forms?.signupJson ?? '{}', valueType: 'json' },
    ],
    banners: (s) => [{ key: 'banners.rules', value: s.banners?.rulesJson ?? '{"rules":[]}', valueType: 'json' }],
    checkin: (s) => [
        { key: 'checkin.birthdayMessage', value: s.checkin?.birthdayMessageJson ?? '{}', valueType: 'json' },
        {
            key: 'checkin.duplicateWindowMinutes',
            value: String(s.checkin?.duplicateWindowMinutes ?? DEFAULT_CHECKIN_DUPLICATE_WINDOW_MINUTES),
            valueType: 'number',
        },
    ],
};

const GROUP_LABEL: Record<CopyGroup, string> = {
    fees: 'joining fee',
    forms: 'forms',
    banners: 'banners',
    checkin: 'check-in message and repeat-scan window',
};

export const ALL_COPY_GROUPS: CopyGroup[] = ['fees', 'forms', 'banners', 'checkin'];

// Build the settings-update rows that copy the given config groups from a source
// tenant's settings. Shared by the per-section copy button and the create-client
// wizard's copy-on-create step.
export function buildCopyRows(groups: CopyGroup[], settings: ClientSettings): ClientSettingUpdate[] {
    return groups.flatMap((g) => GROUP_ROWS[g](settings));
}

interface Props {
    targetClientId: number;
    groups: CopyGroup[];
    onCopied: () => void;
}

// "Copy from another club" action for a settings section. Fetches a source
// tenant's /config and replays the selected config groups onto the target via
// the generic settings PUT (composes existing GET + PUT — no new endpoint).
export const CopyFromClientButton: React.FC<Props> = ({ targetClientId, groups, onCopied }) => {
    const fortiusService = useFortiusClientService();
    const configService = useClientConfigService();
    const [open, setOpen] = useState(false);
    const [clients, setClients] = useState<ClientSummary[]>([]);
    const [sourceId, setSourceId] = useState<number | undefined>(undefined);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const groupNames = groups.map((g) => GROUP_LABEL[g]).join(', ');

    const openModal = useCallback(async () => {
        setError(null);
        setSourceId(undefined);
        setOpen(true);
        try {
            const all = await fortiusService.listClients();
            // Can't copy a tenant onto itself.
            setClients(all.filter((c) => c.id !== targetClientId));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load clubs.');
        }
    }, [fortiusService, targetClientId]);

    const handleCopy = async () => {
        if (sourceId === undefined) return;
        setBusy(true);
        setError(null);
        try {
            const config = await configService.getConfig(sourceId);
            if (!config) throw new Error('Source club has no configuration.');
            const rows = buildCopyRows(groups, config.settings);
            await configService.updateSettings(targetClientId, { settings: rows });
            setOpen(false);
            onCopied();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to copy settings.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Button data-testid="settings-copy-from-client-btn" onClick={openModal}>
                Copy from another club…
            </Button>
            <Modal
                title="Copy configuration from another club"
                open={open}
                onCancel={() => setOpen(false)}
                onOk={handleCopy}
                okText="Copy"
                okButtonProps={{ disabled: sourceId === undefined, loading: busy }}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>
                        This overwrites this club's {groupNames} with the selected club's configuration.
                    </Text>
                    <Select
                        data-testid="settings-copy-source-select"
                        style={{ width: '100%' }}
                        placeholder="Select a club to copy from"
                        value={sourceId}
                        onChange={setSourceId}
                        options={clients.map((c) => ({ value: c.id, label: c.name }))}
                    />
                    {error && <Alert type="error" message={error} data-testid="settings-copy-error" />}
                </Space>
            </Modal>
        </>
    );
};
